import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { AuditLog, AuditAction } from '../../types';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

interface AuditLogItemProps {
  log: AuditLog;
  showDetails?: boolean;
}

const AuditLogItem: React.FC<AuditLogItemProps> = ({ log, showDetails = true }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getActionIcon = (action: AuditAction) => {
    const iconClass = 'w-8 h-8 rounded-full flex items-center justify-center';
    
    switch (action) {
      case 'CREATE':
        return (
          <div className={`${iconClass} bg-green-100`}>
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
        );
      case 'UPDATE':
        return (
          <div className={`${iconClass} bg-blue-100`}>
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
        );
      case 'DELETE':
        return (
          <div className={`${iconClass} bg-red-100`}>
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
        );
      default:
        return (
          <div className={`${iconClass} bg-gray-100`}>
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const getActionText = (): string => {
    const userName = log.userName || 'Usuario';
    
    switch (log.action) {
      case 'CREATE':
        return `${userName} creó este registro`;
      case 'UPDATE':
        return `${userName} actualizó este registro`;
      case 'DELETE':
        return `${userName} eliminó este registro`;
      default:
        return `${userName} realizó una acción`;
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = parseISO(dateString);
      return format(date, "dd/MM/yyyy HH:mm", { locale: es });
    } catch {
      return dateString;
    }
  };

  const formatKey = (key: string): string => {
    const keyMap: Record<string, string> = {
      name: 'Nombre',
      description: 'Descripción',
      amount: 'Monto',
      date: 'Fecha',
      provider: 'Proveedor',
      category_name: 'Categoría',
      subcategory_name: 'Subcategoría',
      project_name: 'Proyecto',
      budget: 'Presupuesto',
      status: 'Estado',
      is_active: 'Activo',
      subscription_plan: 'Plan',
      subscription_status: 'Estado de Suscripción',
      role: 'Rol',
      email: 'Email',
      first_name: 'Nombre',
      last_name: 'Apellido',
      phone_number: 'Teléfono',
      due_date: 'Fecha de Vencimiento',
      attachment_ids: 'Adjuntos',
      assets: 'Activos',
      portal_enabled: 'Portal Habilitado',
      custom_subdomain: 'Subdominio',
      display_name: 'Nombre',
      field_values: 'Valores',
      invoice_number: 'N° Factura',
      invoice_date: 'Fecha Factura',
      client_name: 'Cliente',
      client_rut: 'RUT Cliente',
      client_address: 'Dirección Cliente',
      client_email: 'Email Cliente',
      net_amount: 'Monto Neto',
      tax_amount: 'IVA',
      total_amount: 'Monto Total',
      rut: 'RUT',
      address: 'Dirección',
      phone: 'Teléfono',
    };
    return keyMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const renderValueChange = (key: string, oldValue: any, newValue: any) => {
    // Skip if values are the same
    if (JSON.stringify(oldValue) === JSON.stringify(newValue)) {
      return null;
    }

    const formatValue = (value: any): string => {
      if (value === null || value === undefined) return '-';
      if (typeof value === 'boolean') return value ? 'Sí' : 'No';
      if (typeof value === 'object') return JSON.stringify(value, null, 2);
      return String(value);
    };

    return (
      <div key={key} className="text-sm">
        <span className="font-medium text-gray-700">{formatKey(key)}:</span>
        <div className="ml-4 mt-1">
          {oldValue !== undefined && (
            <div className="text-red-600">
              <span className="font-medium">Antes:</span> {formatValue(oldValue)}
            </div>
          )}
          {newValue !== undefined && (
            <div className="text-green-600">
              <span className="font-medium">Después:</span> {formatValue(newValue)}
            </div>
          )}
        </div>
      </div>
    );
  };

  const hasDetails = (log.oldValues && Object.keys(log.oldValues).length > 0) ||
                     (log.newValues && Object.keys(log.newValues).length > 0);

  return (
    <div className="flex gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
      <div className="flex-shrink-0">{getActionIcon(log.action)}</div>
      
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{getActionText()}</p>
        <p className="text-xs text-gray-500 mt-1">{formatDate(log.createdAt)}</p>

        {showDetails && hasDetails && (
          <>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-2"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-3 h-3" />
                  Ocultar detalles
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" />
                  Ver detalles
                </>
              )}
            </button>

            {isExpanded && (
              <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200 space-y-2">
                {log.action === 'UPDATE' && log.oldValues && log.newValues && (
                  <>
                    {Object.keys({ ...log.oldValues, ...log.newValues }).map((key) =>
                      renderValueChange(key, log.oldValues?.[key], log.newValues?.[key])
                    )}
                  </>
                )}

                {log.action === 'CREATE' && log.newValues && (
                  <>
                    {Object.entries(log.newValues).map(([key, value]) => (
                      <div key={key} className="text-sm">
                        <span className="font-medium text-gray-700">
                          {formatKey(key)}:
                        </span>
                        <span className="ml-2 text-gray-900">
                          {typeof value === 'boolean' ? (value ? 'Sí' : 'No') : typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </span>
                      </div>
                    ))}
                  </>
                )}

                {log.action === 'DELETE' && log.oldValues && (
                  <>
                    {Object.entries(log.oldValues).map(([key, value]) => (
                      <div key={key} className="text-sm">
                        <span className="font-medium text-gray-700">
                          {formatKey(key)}:
                        </span>
                        <span className="ml-2 text-gray-900">
                          {typeof value === 'boolean' ? (value ? 'Sí' : 'No') : typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogItem;
